import { Router } from '~/core';
import { Allocation } from './Allocation';
import { BaseModel } from './BaseModel';
import { Egg, EggSteamJoinButton } from './Egg';
import { Features } from './Features';
import { Node } from './Node';
import { ServerTemplate } from './ServerTemplate';
import { ServerTransfer } from './ServerTransfer';
import { User } from './User';

export class Server extends BaseModel {
    // Mirrors the Server::STATE_* constants in App\Models\Server (the `installed` column).
    public static readonly STATE_NOT_INSTALLED = 0;
    public static readonly STATE_INSTALLED = 1;
    public static readonly STATE_FAILED_INSTALL = 2;
    public static readonly STATE_DEPLOYING_BACKUP = 3;
    public static readonly STATE_FAILED_REINSTALL = 4;

    public uuid = '';
    public uuidShort = '';
    public slug: string | null = null;
    public name = '';
    public description = '';
    public limits = {
        memory: -1,
        swap: -1,
        disk: -1,
        io: -1,
        cpu: -1,
        cpuPinning: [],
        cpuBurst: null as number | null,
        cpuPriority: null as number | null,
        cpuPeriod: null as number | null,
    };
    public featureLimits = {
        databases: -1,
        allocations: -1,
        backupMegabytes: -1,
        backupCount: null as number | null,
    };
    public monitor = false;
    public supportOp = false;
    public isFavorite = false;

    // Admin only props
    public id = -1;
    public legacyId: number | null = null;
    public externalId?: string;
    public startup = '';
    public image = '';
    public suspended = false;
    public installed = -1;
    public updating = false;
    public moving = false;
    public importing = false;
    public oomDisabled = false;
    public forceOutgoingIp = false;
    public useRamdiskTmpfs = true;
    public disableGameQueries = false;
    public serverTemplateId: string | null = null;
    public templateDiverged = false;

    public isSplit = false;
    public syncSubusers = false;
    public parentServer: { identifier: string; name: string } | null = null;
    public splits: {
        identifier: string;
        name: string;
        resources: { cpu: number; memory: number; disk: number; swap: number };
        minimum: { cpu: number | null; memory: number | null; disk: number | null };
    }[] = [];

    // Catalog-driven flat map of every build/limit field, keyed by camelCased field name.
    public build: Record<string, unknown> = {};

    public get egg(): Egg {
        return this.getRelationship('egg');
    }

    public get originalEgg(): Egg | null {
        return this.getRelationship('originalEgg') || null;
    }

    public get serverTemplate(): ServerTemplate | undefined {
        return this.getRelationship('serverTemplate');
    }

    public get node(): Node {
        return this.getRelationship('node');
    }

    /**
     * Whether this server's egg carries the given tag slug. Tags replace the
     * former nest shortName as the game identifiers that drive feature gating.
     */
    public hasTag(slug: string): boolean {
        return this.egg?.tags?.some((tag) => tag.slug === slug) ?? false;
    }

    /**
     * Whether this server is affected by any of the given tag slugs. A server is
     * affected when the slug is carried by its node OR its egg, mirroring how the
     * backend resolves tag-targeted announcements.
     */
    public matchesAnyTag(slugs: string[]): boolean {
        if (slugs.length === 0) {
            return false;
        }

        const wanted = new Set(slugs);

        return (this.node?.tags || []).some((tag) => wanted.has(tag.slug)) || (this.egg?.tags || []).some((tag) => wanted.has(tag.slug));
    }

    public get isLocked(): boolean {
        return this.installed !== 1 || this.moving || this.updating || this.importing;
    }

    public get installFailed(): boolean {
        return this.installed === Server.STATE_FAILED_INSTALL || this.installed === Server.STATE_FAILED_REINSTALL;
    }

    public get allocations(): Allocation[] {
        return this.getRelationship('allocations') || [];
    }

    public primaryAllocation(): Allocation {
        return this.allocations.find((a) => a.primary)!;
    }

    public get features(): Features {
        return this.getRelationship('features');
    }

    public get permissions(): string[] {
        return this.getRelationship('permissions') || [];
    }

    public getRouteName() {
        return 'server';
    }

    public getSlugID(): string | null {
        return this.slug;
    }

    public getRouteID(): string {
        // Both admin and client contexts now use uuidShort for routing
        return this.uuidShort;
    }

    public openConsolePopup() {
        const { href } = Router.resolve({
            name: 'server.console',
            params: { server: this.uuidShort },
        });

        window.open(href, 'Console', 'width=600,height=400');
    }

    public get joinURL() {
        // Grandfathered default behaviour.
        if (this.egg.steamJoinButton == null && (this.hasTag('srcds') || this.hasTag('rust'))) {
            return `steam://connect/${this.primaryAllocation().displayName()}`;
        }

        switch (this.egg.steamJoinButton) {
            case EggSteamJoinButton.GAME_PORT:
                return `steam://connect/${this.primaryAllocation().displayName()}`;
            case EggSteamJoinButton.GAME_PORT_PLUS_N: {
                const port = this.primaryAllocation().port + (this.egg.steamJoinButtonPort || 0);
                if (port > 0 && port <= 65535) {
                    return `steam://connect/${this.primaryAllocation().displayName(false)}:${port}`;
                }
            }
            case EggSteamJoinButton.STATIC_PORT:
                return `steam://connect/${this.primaryAllocation().displayName(false)}:${this.egg.steamJoinButtonPort}`;
        }
        return null;
    }

    public get txAdminURL() {
        if (this.hasTag('fivem')) {
            // FIXME this returns null sometimes
            const ip = this.allocations.find((a) => !a.primary)?.displayName();
            if (!ip) return null;

            return `http://${ip}`;
        }
    }

    public get user(): User {
        return this.getRelationship('user');
    }

    public get transfer(): ServerTransfer | null {
        return this.getRelationship('transfer') || null;
    }
}
